import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  iconoirBrainElectricity,
  iconoirCodeBrackets,
  iconoirCompass,
  iconoirDesignPencil,
  iconoirFlask,
  iconoirHeart,
  iconoirLightBulb,
  iconoirOpenBook,
  iconoirRefreshDouble,
  iconoirRocket,
} from '@ng-icons/iconoir';
import { TranslatePipe } from '@ngx-translate/core';
import { LangRouterLink } from '@pyg-directives/lang-router-link.directive';
import { InnovationParticleCanvasComponent } from './innovation-particle-canvas.component';

@Component({
  selector: 'app-innovation',
  templateUrl: './innovation.component.html',
  styleUrls: ['./innovation.component.scss'],
  imports: [
    TranslatePipe,
    LangRouterLink,
    NgIcon,
    InnovationParticleCanvasComponent,
  ],
  providers: [
    provideIcons({
      iconoirLightBulb,
      iconoirFlask,
      iconoirRefreshDouble,
      iconoirCodeBrackets,
      iconoirOpenBook,
      iconoirCompass,
      iconoirRocket,
      iconoirBrainElectricity,
      iconoirHeart,
      iconoirDesignPencil,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InnovationComponent implements AfterViewInit, OnDestroy {
  @ViewChildren('reveal') revealElements!: QueryList<ElementRef<HTMLElement>>;

  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private revealObserver: IntersectionObserver | null = null;

  readonly serviceItems = [
    { key: 'consulting', icon: 'iconoirLightBulb' },
    { key: 'prototyping', icon: 'iconoirFlask' },
    { key: 'optimization', icon: 'iconoirRefreshDouble' },
    { key: 'engineering', icon: 'iconoirCodeBrackets' },
    { key: 'training', icon: 'iconoirOpenBook' },
  ];

  readonly approachItems = [
    { key: 'business_driven', icon: 'iconoirCompass' },
    { key: 'lean_agile', icon: 'iconoirRocket' },
    { key: 'commitment', icon: 'iconoirBrainElectricity' },
  ];

  readonly valuesItems = [
    { key: 'humans_first', icon: 'iconoirHeart' },
    { key: 'embrace_change', icon: 'iconoirBrainElectricity' },
    { key: 'simplify', icon: 'iconoirDesignPencil' },
    { key: 'always_learning', icon: 'iconoirOpenBook' },
  ];

  readonly differentiatorItems = [
    { key: 'holistic' },
    { key: 'rapid_value' },
    { key: 'empowerment' },
  ];

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.initScrollReveal();
  }

  ngOnDestroy(): void {
    this.revealObserver?.disconnect();
  }

  private initScrollReveal(): void {
    this.revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('revealed');
            this.revealObserver!.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
    );

    this.revealElements.forEach((el) => this.revealObserver!.observe(el.nativeElement));
  }
}
